import { Message } from '@arco-design/web-vue'
import { useSysStore, useUserStore } from '@/store'

export type GlobalWsChannel = 'WAIT_SIGN_IN_LIST' | string

export interface GlobalWsMessage {
  channel?: string
  topic?: string
  event?: string
  type?: string
  payload?: any
  data?: any
  code?: number | string
  message?: string
  [key: string]: any
}

export type GlobalWsHandler = (message: GlobalWsMessage) => void
type GlobalWsErrorReason = 'connect_fail' | 'disconnected' | 'connect_error' | 'token_invalid' | 'heartbeat_fail' | 'reconnect_limit'
type GlobalWsErrorHandler = (reason: GlobalWsErrorReason, payload?: unknown) => void

interface GlobalWebSocketConfig {
  reconnectInterval?: number
  maxReconnectAttempts?: number
  heartbeatInterval?: number
}

interface GlobalWebSocketAuth {
  token?: string
  studentId?: string | number
}

class GlobalWebSocketClient {
  private ws: WebSocket | null = null
  private token = ''
  private studentId = ''
  private lastToken = ''
  private lastStudentId = ''
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectAttempts = 0
  private isManualClose = false
  private isConnected = false
  private isConnecting = false
  private initialized = false
  private authErrorHandled = false
  private readonly handlers = new Map<string, Set<GlobalWsHandler>>()
  private readonly wildcardHandlers = new Set<GlobalWsHandler>()
  private readonly subscribedChannels = new Set<string>()
  private readonly errorHandlers = new Set<GlobalWsErrorHandler>()
  private config: Required<GlobalWebSocketConfig> = {
    reconnectInterval: 3000,
    maxReconnectAttempts: 5,
    heartbeatInterval: 5000
  }

  private get baseUrl() {
    const rawUrl = String(import.meta.env.VITE_WS_BASE_URL || import.meta.env.VITE_API_BASE_URL || '')
      .trim()
      .replace(/^['"]|['"]$/g, '')
    if (!rawUrl) {
      return ''
    }
    const normalizedProtocol = rawUrl
      .replace(/^https:\/\//, 'wss://')
      .replace(/^http:\/\//, 'ws://')
    const normalizedPath = normalizedProtocol.replace(/\/$/, '')
    return normalizedPath.endsWith('/ws') ? normalizedPath.slice(0, -3) : normalizedPath
  }

  init(config?: GlobalWebSocketConfig) {
    if (this.initialized) {
      return
    }
    this.initialized = true
    this.config = {
      ...this.config,
      ...config
    }
  }

  setAuth(auth: GlobalWebSocketAuth) {
    const nextToken = auth.token || ''
    const nextStudentId = auth.studentId ? String(auth.studentId) : ''
    const authChanged = this.token !== nextToken || this.studentId !== nextStudentId

    this.token = nextToken
    this.studentId = nextStudentId
    this.authErrorHandled = false

    if (!this.token || !this.studentId) {
      this.close()
      return
    }

    if (authChanged && this.ws) {
      this.reconnectWithLatestAuth()
      return
    }

    this.connect()
  }

  subscribe(channel: string, handler: GlobalWsHandler) {
    if (!this.handlers.has(channel)) {
      this.handlers.set(channel, new Set())
    }
    this.handlers.get(channel)?.add(handler)
    this.subscribedChannels.add(channel)
    this.sendSubscribe([channel])

    return () => {
      this.handlers.get(channel)?.delete(handler)
      if (!this.handlers.get(channel)?.size) {
        this.handlers.delete(channel)
        this.subscribedChannels.delete(channel)
      }
    }
  }

  subscribeAll(handler: GlobalWsHandler) {
    this.wildcardHandlers.add(handler)
    return () => {
      this.wildcardHandlers.delete(handler)
    }
  }

  subscribeError(handler: GlobalWsErrorHandler) {
    this.errorHandlers.add(handler)
    return () => {
      this.errorHandlers.delete(handler)
    }
  }

  private connect() {
    if (!this.token || !this.studentId) {
      return
    }
    if (this.isConnected || this.isConnecting) {
      return
    }
    const wsUrl = this.buildWsUrl()
    if (!wsUrl) {
      return
    }

    this.clearReconnectTimer()
    this.isManualClose = false
    this.isConnecting = true
    this.lastToken = this.token
    this.lastStudentId = this.studentId

    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl)
    } catch (error) {
      this.isConnecting = false
      this.emitError('connect_fail', error)
      this.scheduleReconnect()
      return
    }

    this.ws = ws

    ws.onopen = () => {
      if (this.ws !== ws) {
        return
      }
      this.isConnected = true
      this.isConnecting = false
      this.reconnectAttempts = 0
      this.startHeartbeat()
      this.sendSubscribe()
    }

    ws.onmessage = event => {
      this.handleMessage(event.data)
    }

    ws.onclose = event => {
      if (this.isManualClose) {
        this.clearCurrentSocket(ws)
        return
      }
      if (this.isTokenInvalidCloseEvent(event)) {
        this.clearCurrentSocket(ws)
        this.handleTokenInvalid({
          type: 'UNAUTHORIZED',
          code: event.code,
          message: event.reason
        })
        return
      }
      this.handleDisconnect(ws, event, 'disconnected')
    }

    ws.onerror = event => {
      const reason = this.isConnected ? 'connect_error' : 'connect_fail'
      this.handleDisconnect(ws, event, reason)
    }
  }

  private buildWsUrl() {
    if (!this.baseUrl || !this.studentId) {
      return ''
    }
    return `${this.baseUrl}/ws/student/${encodeURIComponent(this.studentId)}?token=${encodeURIComponent(this.token)}`
  }

  private handleMessage(rawData: MessageEvent['data']) {
    const message = this.parseMessage(rawData)
    if (!message) {
      return
    }
    if (this.isHeartbeatPing(message)) {
      this.replyHeartbeat(message.type || '')
      return
    }
    if (this.isHeartbeatPong(message)) {
      return
    }
    if (this.isTokenInvalidMessage(message)) {
      this.handleTokenInvalid(message)
      return
    }
    const channel = this.resolveChannel(message)
    if (channel) {
      this.handlers.get(channel)?.forEach(handler => handler(message))
    }
    this.wildcardHandlers.forEach(handler => handler(message))
  }

  private isHeartbeatPing(message: GlobalWsMessage) {
    return message.type === 'PING' || message.type === 'HEARTBEAT_PING'
  }

  private isHeartbeatPong(message: GlobalWsMessage) {
    return message.type === 'PONG' || message.type === 'HEARTBEAT_PONG'
  }

  private replyHeartbeat(messageType: string) {
    const pongType = messageType === 'HEARTBEAT_PING' ? 'HEARTBEAT_PONG' : 'PONG'
    this.sendRaw({
      type: pongType
    }, 'heartbeat_fail')
  }

  private isTokenInvalidMessage(message: GlobalWsMessage) {
    if (message.type === 'FORCED_DISCONNECT' || message.type === 'TOKEN_INVALID' || message.type === 'UNAUTHORIZED') {
      return true
    }
    if (String(message.code || '') === '401') {
      return true
    }
    return false
  }

  private isTokenInvalidCloseEvent(event: CloseEvent) {
    return event.code === 1008
  }

  private handleTokenInvalid(message: GlobalWsMessage) {
    if (this.authErrorHandled) {
      return
    }
    this.authErrorHandled = true
    this.emitError('token_invalid', message)
    this.close()
    const userStore = useUserStore()
    userStore.clearCache()
    Message.error(message.message || message.text || '登录已失效，请重新登录')
    setTimeout(() => {
      if (useSysStore().isInMp) {
        wx.miniProgram.reLaunch({
          url: '/pages/login/index'
        })
      } else if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }, 1200)
  }

  private resolveChannel(message: GlobalWsMessage) {
    return message.channel || message.topic || ''
  }

  private sendSubscribe(channels?: string[]) {
    const channelList = channels?.length ? channels : Array.from(this.subscribedChannels)
    if (!channelList.length) {
      return
    }
    this.sendRaw({
      type: 'SUBSCRIBE',
      channels: channelList
    })
  }

  private emitError(reason: GlobalWsErrorReason, payload?: unknown) {
    this.errorHandlers.forEach(handler => handler(reason, payload))
  }

  private scheduleReconnect() {
    if (this.isManualClose) {
      return
    }
    if (!this.getAuth()) {
      return
    }
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.emitError('reconnect_limit', {
        token: this.lastToken,
        studentId: this.lastStudentId
      })
      return
    }
    this.clearReconnectTimer()
    this.reconnectAttempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, this.config.reconnectInterval)
  }

  private handleDisconnect(ws: WebSocket, payload: unknown, reason: GlobalWsErrorReason) {
    if (this.ws !== ws) {
      return
    }
    this.clearCurrentSocket(ws)
    this.emitError(reason, payload)
    this.scheduleReconnect()
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected) {
        return
      }
      this.sendRaw({
        type: 'HEARTBEAT_PING'
      }, 'heartbeat_fail')
    }, this.config.heartbeatInterval)
  }

  private stopHeartbeat() {
    if (!this.heartbeatTimer) {
      return
    }
    clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
  }

  private clearReconnectTimer() {
    if (!this.reconnectTimer) {
      return
    }
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
  }

  private clearCurrentSocket(ws?: WebSocket) {
    if (ws && this.ws !== ws) {
      return
    }
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onclose = null
      this.ws.onerror = null
    }
    this.ws = null
    this.isConnected = false
    this.isConnecting = false
    this.stopHeartbeat()
  }

  private reconnectWithLatestAuth() {
    this.clearReconnectTimer()
    const currentWs = this.ws
    this.isManualClose = true
    this.clearCurrentSocket()
    if (currentWs && (currentWs.readyState === WebSocket.OPEN || currentWs.readyState === WebSocket.CONNECTING)) {
      currentWs.close()
    }
    this.isManualClose = false
    this.connect()
  }

  private sendRaw(message: Record<string, any>, failReason?: Extract<GlobalWsErrorReason, 'heartbeat_fail'>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false
    }
    try {
      this.ws.send(JSON.stringify(message))
      return true
    } catch (error) {
      if (failReason && this.ws) {
        this.handleDisconnect(this.ws, error, failReason)
      }
      return false
    }
  }

  private getAuth() {
    const token = this.token || this.lastToken
    const studentId = this.studentId || this.lastStudentId
    if (!token || !studentId) {
      return null
    }
    return {
      token,
      studentId
    }
  }

  private parseMessage(rawData: MessageEvent['data']) {
    if (typeof rawData !== 'string') {
      return rawData as GlobalWsMessage
    }
    try {
      return JSON.parse(rawData) as GlobalWsMessage
    } catch (error) {
      console.error(error)
      return null
    }
  }

  close() {
    this.isManualClose = true
    this.clearReconnectTimer()
    this.stopHeartbeat()
    this.reconnectAttempts = 0
    const currentWs = this.ws
    this.clearCurrentSocket()
    if (currentWs && (currentWs.readyState === WebSocket.OPEN || currentWs.readyState === WebSocket.CONNECTING)) {
      currentWs.close()
    }
  }

  getStudentId(): string {
    return this.studentId || this.lastStudentId
  }
}

const globalWebSocketClient = new GlobalWebSocketClient()

export const GLOBAL_WS_CHANNEL = {
  WAIT_SIGN_IN_LIST: 'WAIT_SIGN_IN_LIST'
}

export function initGlobalWebSocket(config?: GlobalWebSocketConfig) {
  globalWebSocketClient.init(config)
}

export function setGlobalWebSocketAuth(auth: GlobalWebSocketAuth) {
  globalWebSocketClient.setAuth(auth)
}

export function subscribeGlobalWebSocketChannel(channel: GlobalWsChannel, handler: GlobalWsHandler) {
  return globalWebSocketClient.subscribe(channel, handler)
}

export function subscribeGlobalWebSocketMessage(handler: GlobalWsHandler) {
  return globalWebSocketClient.subscribeAll(handler)
}

export function subscribeGlobalWebSocketError(handler: GlobalWsErrorHandler) {
  return globalWebSocketClient.subscribeError(handler)
}

export function closeGlobalWebSocket() {
  globalWebSocketClient.close()
}

export function getGlobalWebSocketStudentId(): string {
  return globalWebSocketClient.getStudentId()
}
